function asyncGeneratorStep(n, t, e, r, o, a, c) { try { var i = n[a](c), u = i.value; } catch (n) { return void e(n); } i.done ? t(u) : Promise.resolve(u).then(r, o); }
function _asyncToGenerator(n) { return function () { var t = this, e = arguments; return new Promise(function (r, o) { var a = n.apply(t, e); function _next(n) { asyncGeneratorStep(a, r, o, _next, _throw, "next", n); } function _throw(n) { asyncGeneratorStep(a, r, o, _next, _throw, "throw", n); } _next(void 0); }); }; }
import { useCallback, useEffect, useRef } from 'react';
import Log from '@deephaven/log';
import { useIsMountedRef } from "./useIsMountedRef.js";
var log = Log.module('useAsyncInterval');

/**
 * Calls the given async callback at a target interval.
 *
 * If the callback takes less time than the target interval, the target interval
 * for the next tick will be adjusted to target the remaining time in the current
 * interval.
 *
 * e.g. If the target interval is 1000ms, and the callback takes 50ms to resolve,
 * the next tick will be scheduled for 950ms from now via `setTimeout(callback, 950)`.
 *
 * If the callback takes longer than the target interval, the next tick will be
 * scheduled immediately via `setTimeout(callback, 0)`. In such cases, the time
 * between ticks may be > than the target interval, but this guarantees that
 * a callback won't be scheduled until after the previous one has resolved.
 * @param callback Callback to call at the target interval
 * @param targetIntervalMs Target interval in milliseconds to call the callback
 */
export function useAsyncInterval(callback, targetIntervalMs) {
  var isMountedRef = useIsMountedRef();
  var setTimeoutRef = useRef(0);
  var trackingCountRef = useRef(0);
  var trackingStartedRef = useRef(null);
  var tick = useCallback(/*#__PURE__*/_asyncToGenerator(function* () {
    var now = Date.now();
    trackingCountRef.current += 1;

    // If this is our first tick, treat it as if we've already waited the full
    // interval, otherwise calculate the elapsed time since the last tick
    var elapsedSinceLastTick = trackingStartedRef.current == null ? targetIntervalMs : now - trackingStartedRef.current;
    trackingStartedRef.current = now;
    try {
      yield callback();
    } catch (err) {
      log.error('A tick error occurred:', err);
    }
    if (!isMountedRef.current) {
      return;
    }
    elapsedSinceLastTick += Date.now() - trackingStartedRef.current;

    // Calculate any elapsed time beyond the target interval.
    var overage = Math.max(0, elapsedSinceLastTick - targetIntervalMs);
    var nextTickInterval = Math.max(0, targetIntervalMs - overage);
    setTimeoutRef.current = window.setTimeout(tick, nextTickInterval);
  }), [callback, isMountedRef, targetIntervalMs]);
  useEffect(() => {
    trackingStartedRef.current = null;
    tick();
    return () => {
      window.clearTimeout(setTimeoutRef.current);
    };
  }, [targetIntervalMs, tick]);
}
export default useAsyncInterval;
//# sourceMappingURL=useAsyncInterval.js.map